// Adım 2 — Eşleştir (M16 T11; mockup 05, kural 7 triage, Eng 6A/10). Sayfa sekmeleri + kolon
// çipleri (+ düzenleme paneli) + sayaç + filtre çipleri + toplu kabul/atla + arama/kategori +
// 25 satırlık sayfalar. Kararlar 1,5 sn sonra `PUT /mapping` ile taslağa gider ("Taslağı
// kaydet" anında). İleri: eşlenmemiş işletme kolonu varken disabled; bekleyen karar sayısı yazılır.
// Tur düzeltmeleri: yıl kolonu yoksa zorunlu "Yıl", grup müşteride işletme kolonu yoksa zorunlu
// "İşletme" seçici (B-02/B-03, İleri kilidi); kategori düğümleri hedef listesinde yok (P2-9);
// müşteri modunda yalnız açık hedefler + salt okunur/create önerileri skip'e çevrilir (P1-2/P1-3).
// Gerçek LLM turu: yıl SAYFA bazlı (P2-6 — tek sayfa yüzünden kilit yok), sayfa sekmesinde "Bu
// sayfayı atla" (P2-8 — bilanço gibi sayfalar plana girmez; çıkarım önerisi amber satırda), öneri
// aşaması uyarıları (P1-1 — "N satır için model öneri vermedi") amber kutu; sayfa sekmesi/kolon
// modeli rowId önekiyle değil grid dizi indeksiyle eşlenir (`sheetIndexByRowId`).
// güvenlik/UX taraması 2026-10-08 (600 satır sınırı): sayfa sekmeleri, varsayılan yıl/işletme
// fieldset'i ve sayfalama/otomatik kayıt alt satırı `ImportMappingPanels.tsx`'te (davranış birebir);
// sayaç/filtre/toplu işlem/arama satırı `ImportMappingToolbar.tsx`'te (kırılım işi, salt taşıma).
// Kırılım (spec §7.3, DR7 — yalnız iç sihirbaz): üstte "Önerilen kırılımlar" tablosu
// (`ImportBreakdownSuggestions`), satırda "Kırılım olarak ekle" alt satırı; kararlar eşleşmeyi
// yeniden yazar ve aynı 1,5 sn otomatik kayıttan geçer (eksik alanlı satır `savableMapping`).

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  ClientEntity,
  KpiImportColumn,
  KpiImportGrid,
  KpiMappingAction,
  KpiMappingDefaults,
  KpiMappingRow,
  KpiSetIndicator,
} from "@mahir/shared";
import { listClientEntities } from "../../../api/client-entities";
import {
  INTERNAL_IMPORT_API,
  KpiImportRequestError,
  type ImportApi,
  type KpiImportJobDto,
} from "../../../api/kpi-import";
import { useImportBreakdown } from "../../../hooks/use-import-breakdown";
import {
  decideSuggestions,
  patchBreakdownFields,
  savableMapping,
  toggleSuggestionComplete,
  type BreakdownFieldPatch,
  type SuggestionDecision,
} from "../../../lib/kpi-import-breakdown";
import { KPI_IMPORT_COPY } from "../../../lib/kpi-import-copy";
import { KPI_COPY } from "../../../lib/kpi-ui";
import {
  DEFAULT_MAPPING_FILTER,
  acceptHighConfidence,
  buildCategoryIndex,
  columnLetter,
  countMapping,
  decideRow,
  filterMappingRows,
  formatIssues,
  missingDefaults,
  groupTotalSheets,
  sheetsNeedingEntity,
  paginate,
  sanitizeClientMapping,
  selectableEntities,
  sheetIndexByRowId,
  sheetsNeedingYear,
  skipRows,
  sortForTriage,
  suggestedSkipSheets,
  targetOptionsFrom,
  toggleSkippedSheet,
  unresolvedEntityNames,
  type MappingFilter,
} from "../../../lib/kpi-import-wizard";
import { LoadingState } from "../../common/StateSurface";
import { amberBox, btnGhost, btnPrimary, btnSmall, errorBox, pillStyle } from "../kpi-form-styles";
import { ImportBreakdownSuggestions } from "./ImportBreakdownSuggestions";
import { ImportColumnsPanel } from "./ImportColumnsPanel";
import {
  ImportDefaultsFieldset,
  ImportMappingFooter,
  ImportSheetTabs,
} from "./ImportMappingPanels";
import { ImportMappingTable } from "./ImportMappingTable";
import { ImportMappingToolbar } from "./ImportMappingToolbar";
import type { TargetOption } from "./TargetPicker";

const AUTOSAVE_MS = 1500;

export interface ImportMappingStepProps {
  job: KpiImportJobDto;
  grid: KpiImportGrid;
  clientId: string;
  setId: string;
  mapping: KpiMappingRow[];
  columns: KpiImportColumn[][];
  onMapping: (next: KpiMappingRow[]) => void;
  onColumns: (next: KpiImportColumn[][]) => void;
  /** B-02/B-03 — dosyada olmayan yıl/işletme seçimi (taslakla kaydedilir) */
  defaults: KpiMappingDefaults;
  onDefaults: (next: KpiMappingDefaults) => void;
  onBack: () => void;
  onNext: () => void;
  /** T19 — API tabanı (iç / portal) */
  api?: ImportApi;
  /** T19 — müşteri modu: `create` yok, kolon düzenleme yok, eşlenmemiş işletme → danışmana ilet */
  isClient?: boolean;
  /** T19 — portalda işletme listesi uçtan değil grid'den gelir */
  entities?: readonly ClientEntity[] | undefined;
}

export type SaveState =
  | { kind: "idle" }
  | { kind: "saving" }
  | { kind: "saved" }
  | { kind: "failed"; message: string };

export function ImportMappingStep(props: ImportMappingStepProps): JSX.Element {
  const {
    job,
    grid,
    clientId,
    mapping,
    columns,
    onMapping,
    onColumns,
    defaults,
    onDefaults,
    onBack,
    onNext,
  } = props;
  const api = props.api ?? INTERNAL_IMPORT_API;
  const isClient = props.isClient ?? false;
  const givenEntities = props.entities;
  const m = KPI_IMPORT_COPY.mapping;

  const [setOptions, setSetOptions] = useState<TargetOption[] | null>(null);
  const [catalogOptions, setCatalogOptions] = useState<TargetOption[]>([]);
  const [setIndicators, setSetIndicators] = useState<KpiSetIndicator[]>([]);
  const [categoryIndex, setCategoryIndex] = useState(() => buildCategoryIndex([]));
  const [entities, setEntities] = useState<ClientEntity[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Bekleyen karar yoksa varsayılan filtre boş liste göstermesin → "Tümü" (kural 7 triage).
  const [filter, setFilter] = useState<MappingFilter>(() =>
    countMapping(mapping).pending > 0
      ? DEFAULT_MAPPING_FILTER
      : { ...DEFAULT_MAPPING_FILTER, status: "all" },
  );
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [showColumns, setShowColumns] = useState(false);
  const [save, setSave] = useState<SaveState>({ kind: "idle" });
  const dirtyRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestRef = useRef({ mapping, columns, defaults });
  latestRef.current = { mapping, columns, defaults };
  const sanitizedRef = useRef(false);
  // Kırılım "Geri al" — sihirbaz açılışındaki öneri satırları (durum yerel, store'a girmez)
  const initialRef = useRef<ReadonlyMap<string, KpiMappingRow>>(
    new Map(mapping.map((r) => [r.rowId, r])),
  );

  // Set göstergeleri + katalog + işletmeler
  useEffect(() => {
    let alive = true;
    Promise.all([
      api.loadTargets(job.setId, clientId),
      givenEntities ? Promise.resolve([...givenEntities]) : listClientEntities(clientId),
    ])
      .then(([targets, ents]) => {
        if (!alive) return;
        // P2-9: kategori düğümleri listede yok; müşteri modunda yalnız açık hedefler (P1-2)
        setSetOptions(targetOptionsFrom(targets.setIndicators, { isClient }));
        setCatalogOptions(targets.catalog.filter((c) => !c.isCategory));
        setCategoryIndex(buildCategoryIndex(targets.setIndicators));
        setSetIndicators(targets.setIndicators);
        setEntities(ents);
      })
      .catch((err: unknown) => {
        if (alive) setLoadError(err instanceof Error ? err.message : KPI_IMPORT_COPY.genericError);
      });
    return () => {
      alive = false;
    };
  }, [job.setId, clientId, api, givenEntities, isClient]);

  const labelByCode = useMemo(() => {
    const map = new Map(categoryIndex.labelByCode);
    for (const o of catalogOptions) if (!map.has(o.code)) map.set(o.code, o.label);
    return map;
  }, [categoryIndex, catalogOptions]);

  const gridRows = useMemo(
    () => new Map(grid.sheets.flatMap((s) => s.rows.map((r) => [r.rowId, r] as const))),
    [grid],
  );
  const sheetOfRow = useMemo(() => sheetIndexByRowId(grid), [grid]);
  const counts = useMemo(() => countMapping(mapping), [mapping]);
  const visible = useMemo(
    () =>
      sortForTriage(
        filterMappingRows(mapping, filter, {
          categoryByCode: categoryIndex.categoryByCode,
          labelByCode,
          sheetOfRow,
        }),
      ),
    [mapping, filter, categoryIndex, labelByCode, sheetOfRow],
  );
  const pageData = useMemo(() => paginate(visible, page), [visible, page]);
  const skippedSheets = useMemo(() => defaults.skippedSheets ?? [], [defaults.skippedSheets]);
  const unresolved = useMemo(
    () =>
      unresolvedEntityNames(
        columns.filter((_, i) => !skippedSheets.includes(i)),
        entities,
      ),
    [columns, entities, skippedSheets],
  );
  // P2-6 — yıl kolonu olmayan (ve atlanmamış) sayfalar: her biri için ayrı yıl seçici
  const askYearSheets = useMemo(
    () => sheetsNeedingYear(columns, skippedSheets),
    [columns, skippedSheets],
  );
  const askEntitySheets = useMemo(
    () => sheetsNeedingEntity(columns, entities, skippedSheets),
    [columns, entities, skippedSheets],
  );
  const missing = useMemo(
    () => missingDefaults(columns, entities, defaults),
    [columns, entities, defaults],
  );
  const suggestSkip = useMemo(
    () => suggestedSkipSheets(grid, skippedSheets),
    [grid, skippedSheets],
  );
  const groupTotals = useMemo(
    () => groupTotalSheets(grid, entities, skippedSheets),
    [grid, entities, skippedSheets],
  );
  const mappingWarnings = job.mappingWarnings ?? [];
  const entityChoices = useMemo(() => selectableEntities(entities), [entities]);
  const hasSuggestions = useMemo(
    () => mapping.some((r) => r.confidence !== undefined || r.memoryHit),
    [mapping],
  );
  // Kırılım — öneri tablosu, üst gösterge seçenekleri, toplam satırı rozetleri (yalnız iç sihirbaz)
  const bd = useImportBreakdown({
    enabled: !isClient,
    mapping,
    gridRows,
    columns,
    sheetOfRow,
    labelByCode,
    indicators: setIndicators,
  });
  const sheetCounts = useMemo(
    () => grid.sheets.map((s, i) => ({ name: s.name, count: s.rows.length, idx: i })),
    [grid],
  );

  // Autosave (debounce) — taslak sunucuda
  const flush = useCallback(async (): Promise<void> => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (!dirtyRef.current) return;
    dirtyRef.current = false;
    setSave({ kind: "saving" });
    try {
      await api.saveMapping(
        job.id,
        savableMapping(latestRef.current.mapping),
        latestRef.current.columns,
        latestRef.current.defaults,
      );
      setSave({ kind: "saved" });
    } catch (err) {
      dirtyRef.current = true;
      const msg =
        err instanceof KpiImportRequestError && err.issues.length > 0
          ? formatIssues(err.issues).join(" · ")
          : err instanceof Error
            ? err.message
            : KPI_IMPORT_COPY.genericError;
      setSave({ kind: "failed", message: msg });
    }
  }, [job.id, api]);

  const schedule = useCallback((): void => {
    dirtyRef.current = true;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => void flush(), AUTOSAVE_MS);
  }, [flush]);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (dirtyRef.current) void flush();
    },
    [flush],
  );

  // P1-2/P1-3 — müşteri modunda seçilemeyen hedefe giden öneriler bir kez skip'e çevrilir
  useEffect(() => {
    if (!isClient || !setOptions || sanitizedRef.current) return;
    sanitizedRef.current = true;
    const allowed = new Set(setOptions.map((o) => o.code));
    const next = sanitizeClientMapping(mapping, allowed);
    if (next.some((r, i) => r !== mapping[i])) {
      onMapping(next);
      schedule();
    }
  }, [isClient, setOptions, mapping, onMapping, schedule]);

  function updateMapping(next: KpiMappingRow[]): void {
    onMapping(next);
    schedule();
  }
  function updateColumns(next: KpiImportColumn[][]): void {
    onColumns(next);
    schedule();
  }
  function applyDefaults(next: KpiMappingDefaults): void {
    onDefaults(next);
    schedule();
  }
  function updateDefaults(patch: KpiMappingDefaults): void {
    const next: KpiMappingDefaults = { ...defaults };
    if ("defaultEntityId" in patch) {
      if (patch.defaultEntityId === undefined) delete next.defaultEntityId;
      else next.defaultEntityId = patch.defaultEntityId;
    }
    applyDefaults(next);
  }
  function setSkipped(sheetIdx: number, skipped: boolean): void {
    applyDefaults(toggleSkippedSheet(defaults, sheetIdx, skipped));
  }
  function decide(
    rowId: string,
    patch: { action?: KpiMappingAction; targetCode?: string | null },
  ): void {
    updateMapping(mapping.map((r) => (r.rowId === rowId ? decideRow(r, patch) : r)));
  }
  function decideBreakdowns(keys: ReadonlySet<string>, decision: SuggestionDecision): void {
    updateMapping(decideSuggestions(mapping, keys, decision, initialRef.current));
  }
  function patchBreakdown(rowId: string, patch: BreakdownFieldPatch): void {
    updateMapping(mapping.map((r) => (r.rowId === rowId ? patchBreakdownFields(r, patch) : r)));
  }
  function setFilterAndReset(patch: Partial<MappingFilter>): void {
    setFilter((f) => ({ ...f, ...patch }));
    setPage(0);
  }

  async function next(): Promise<void> {
    await flush();
    if (dirtyRef.current) return; // kaydedilemedi — hata görünür, ilerlenmez
    onNext();
  }

  if (loadError) {
    return (
      <div role="alert" style={errorBox}>
        {loadError}
      </div>
    );
  }
  if (!setOptions) return <LoadingState />;

  return (
    <div className="flex flex-col gap-3">
      <div
        className="rounded-xl p-3 text-sm"
        style={{ border: "1px solid var(--color-border)", color: "var(--color-text-2)" }}
      >
        {hasSuggestions ? (isClient ? KPI_IMPORT_COPY.client.intro : m.intro) : m.introNoLlm}
      </div>

      {/* P1-1 — öneri aşaması uyarıları (parça bazlı "model öneri vermedi") */}
      {mappingWarnings.length > 0 ? (
        <div role="status" style={amberBox} data-testid="kpi-import-mapping-warnings">
          <div className="font-semibold mb-1">{m.warningsTitle(mappingWarnings.length)}</div>
          <ul className="flex flex-col gap-1" style={{ paddingLeft: 16, margin: 0 }}>
            {mappingWarnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
          <div className="mt-1" style={{ fontSize: "0.8rem" }}>
            {m.warningsHint}
          </div>
        </div>
      ) : null}

      <ImportBreakdownSuggestions
        suggestions={bd.suggestions}
        onDecide={decideBreakdowns}
        onComplete={(key, complete) =>
          updateMapping(
            toggleSuggestionComplete(mapping, bd.suggestions, setIndicators, key, complete),
          )
        }
      />

      {/* Sayfa sekmeleri + P2-8 sayfa atlama → ImportMappingPanels (2026-10-08 ayrımı) */}
      <ImportSheetTabs
        sheetCounts={sheetCounts}
        mappingCount={mapping.length}
        filterSheet={filter.sheet}
        skippedSheets={skippedSheets}
        suggestSkip={suggestSkip}
        groupTotalSheets={groupTotals}
        grid={grid}
        onSelectSheet={(sheet) => setFilterAndReset({ sheet })}
        onSetSkipped={setSkipped}
      />

      {/* Kolon çipleri + düzenle */}
      <div className="flex items-center gap-2 flex-wrap text-sm">
        <span style={{ color: "var(--color-text-3)" }}>{m.columnsLabel}</span>
        {(columns[filter.sheet ?? 0] ?? []).map((c) => (
          <span
            key={c.index}
            style={{
              ...pillStyle,
              background: "var(--color-bg)",
              color: c.kind === "ignore" ? "var(--color-text-3)" : "var(--color-text)",
            }}
          >
            {columnLetter(c.sourceIndex ?? c.index)}{" "}
            <b
              style={{
                color: c.kind === "ignore" ? "var(--color-text-3)" : "var(--color-primary)",
              }}
            >
              {c.kind === "year"
                ? (c.year?.toString() ?? m.columnKinds.year)
                : c.kind === "entity"
                  ? (c.entityName ?? m.columnKinds.entity)
                  : m.columnKinds[c.kind]}
            </b>
          </span>
        ))}
        {!isClient ? (
          <button
            type="button"
            style={{ ...btnSmall, border: "none", color: "var(--color-primary)", fontWeight: 600 }}
            onClick={() => setShowColumns((v) => !v)}
            aria-expanded={showColumns}
          >
            {showColumns ? m.closeColumns : m.editColumns}
          </button>
        ) : null}
      </div>
      {showColumns && !isClient ? (
        <ImportColumnsPanel
          grid={grid}
          columns={columns}
          entities={entities}
          onChange={updateColumns}
        />
      ) : null}
      {unresolved.length > 0 ? (
        <div role="alert" style={amberBox}>
          {isClient
            ? KPI_COPY.portal.import.entityUnresolved(unresolved)
            : m.entityUnresolved(unresolved)}
        </div>
      ) : null}

      {/* B-02/B-03 — dosyada olmayan yıl / işletme (zorunlu seçim, İleri kilidi); P2-6 yıl sayfa bazlı */}
      <ImportDefaultsFieldset
        askYearSheets={askYearSheets}
        askEntitySheets={askEntitySheets}
        missingCount={missing.length}
        grid={grid}
        defaults={defaults}
        entityChoices={entityChoices}
        onApplyDefaults={applyDefaults}
        onUpdateDefaults={updateDefaults}
        onSetSkipped={setSkipped}
      />

      <ImportMappingToolbar
        counts={counts}
        filter={filter}
        categories={categoryIndex.categories}
        selectedCount={selected.size}
        onFilter={setFilterAndReset}
        onAcceptHigh={() => updateMapping(acceptHighConfidence(mapping))}
        onSkipSelected={() => {
          updateMapping(skipRows(mapping, selected));
          setSelected(new Set());
        }}
      />

      {pageData.total === 0 ? (
        <div
          className="rounded-xl p-6 text-sm text-center"
          style={{ border: "1px dashed var(--color-border)", color: "var(--color-text-3)" }}
        >
          {m.emptyFilter}{" "}
          <button
            type="button"
            style={{ ...btnSmall, border: "none", color: "var(--color-primary)" }}
            onClick={() => setFilterAndReset({ ...DEFAULT_MAPPING_FILTER, status: "all" })}
          >
            {m.clearFilter}
          </button>
        </div>
      ) : (
        <ImportMappingTable
          rows={pageData.items}
          gridRows={gridRows}
          columns={columns}
          selected={selected}
          onToggle={(id, checked) =>
            setSelected((prev) => {
              const n = new Set(prev);
              if (checked) n.add(id);
              else n.delete(id);
              return n;
            })
          }
          onToggleAll={(checked) =>
            setSelected((prev) => {
              const n = new Set(prev);
              for (const r of pageData.items)
                if (checked) n.add(r.rowId);
                else n.delete(r.rowId);
              return n;
            })
          }
          onDecide={decide}
          setOptions={setOptions}
          catalogOptions={catalogOptions}
          labelByCode={labelByCode}
          allowCreate={!isClient}
          sheetOfRow={sheetOfRow}
          {...(isClient
            ? {}
            : {
                breakdown: {
                  parentOptions: bd.parentOptions,
                  groupInSet: bd.groupInSet,
                  totalLines: bd.totalLines,
                  onPatch: patchBreakdown,
                },
              })}
        />
      )}

      <ImportMappingFooter
        pageData={pageData}
        save={save}
        onPrev={() => setPage((p) => p - 1)}
        onNext={() => setPage((p) => p + 1)}
        onSaveDraft={() => {
          dirtyRef.current = true;
          void flush();
        }}
      />
      {save.kind === "failed" ? (
        <div role="alert" style={errorBox}>
          {m.saveFailed}: {save.message}
        </div>
      ) : null}

      <div className="flex items-center justify-between gap-3 flex-wrap mt-2">
        <button type="button" style={btnGhost} onClick={onBack}>
          {m.back}
        </button>
        <button
          type="button"
          style={btnPrimary}
          disabled={unresolved.length > 0 || missing.length > 0 || save.kind === "saving"}
          title={missing.length > 0 ? m.defaults.blockNext : undefined}
          onClick={() => void next()}
        >
          {m.nextStep(counts.pending)}
        </button>
      </div>
    </div>
  );
}
